import { Button, Container, LabeledValue } from 'components/atoms';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { ChangeEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PiDownload, PiFile, PiFileArrowDown, PiTrash } from 'react-icons/pi';
import { ICashFlowForm } from 'services/cash-flow/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { readableFileSize } from 'utils/components/inputs/file';
import { formatDate } from 'utils/global/date.utils';

const CashFlowAddFileForm = () => {
  const { t } = useTranslation();
  const { values, status, setFieldValue }: IFormikContextType<ICashFlowForm> =
    useFormikContext<ICashFlowForm>();
  const inputRef = useRef<HTMLInputElement>(null);

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.currentTarget.files) {
      setFieldValue('file', event.currentTarget.files[0]);
      event.currentTarget.value = '';
    }
  };

  return (
    <div className='flex flex-col w-full gap-4'>
      <Container className='max-w-[900px] self-start'>
        <Container.Header
          title={
            <>
              <PiFile />
              {status?.displayMode
                ? t('cash-flow.file.header')
                : t('cash-flow.file.addHeader')}
            </>
          }>
          {!status?.displayMode && (
            <div className='flex m-1 gap-1'>
              <Button isClear onClick={() => inputRef.current?.click()}>
                <Button.Icon icon={<PiFileArrowDown />} />
                <Button.Text text={t('button.addFile')} />
              </Button>
              <input
                ref={inputRef}
                className='hidden'
                type='file'
                onChange={onInputChange}
              />
              {values.file && (
                <Button
                  isClear
                  variant={BUTTON_VARIANT.remove}
                  onlyIcon
                  onClick={() => setFieldValue('file', undefined)}>
                                    <Button.Description description={t('button.remove')} />

                                    <Button.Icon icon={<PiTrash />} />
                </Button>
              )}
            </div>
          )}
        </Container.Header>

        <Container.Content className='flex flex-row justify-between items-center'>
          {values.file ? (
            <div className='flex flex-col gap-3'>
              <LabeledValue label={t('track.file.name')}>
                {values.file?.name.split('.')[0]}
              </LabeledValue>
              <LabeledValue label={t('track.file.extension')}>
                {values.file?.name.split('.')[1]}
              </LabeledValue>
              <LabeledValue label={t('track.file.size')}>
                {readableFileSize(values.file?.size)}
              </LabeledValue>
              <LabeledValue label={t('track.file.lastModified')}>
                {values.file?.lastModified &&
                  formatDate(new Date(values.file.lastModified).toISOString())}
              </LabeledValue>
            </div>
          ) : values.fileUrl ? (
            <a
              href={values.fileUrl}
              download={values.fileUrl}
              aria-disabled={!values.fileUrl}
              target='_blank'
              rel='noreferrer'>
              <Button type='button' isClear disabled={!values.fileUrl}>
                <Button.Icon icon={<PiDownload />} />
                <Button.Text text={t('button.download')} />
              </Button>
            </a>
          ) : (
            t('cash-flow.file.no-file')
          )}
        </Container.Content>
      </Container>
    </div>
  );
};

export default CashFlowAddFileForm;
